import useGTMDispatch from "@src/app/hooks/useGTMDispatch";
import { useRouteResponse } from "@src/app/hooks/useRouteResponse";
import type { BFFResponseType } from "@src/app/types/bff";
import type { ArticlePageResponse } from "@src/app/types/response";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import ArticleCard from "../ArticleCard";
import SectionContainer from "../SectionContainer/SectionContainer";
import { ThemeEnum } from "../ThemeProvider/ThemeProvider";

/**
 * Article Recommend Articles
 */
export default function ArticleRecommend({ size = 3 }: { size?: number }) {
  const { article } = useRouteResponse("articlePage");
  const sendDataToGTM = useGTMDispatch();
  const hasTrackedRecommendViewRef = useRef(false);

  const [recommendArticles, setRecommendArticles] = useState<
    ArticlePageResponse["recommendArticles"]
  >([]);
  const safeRecommendArticles = recommendArticles || [];

  const theme =
    article?.displayType == "video" ? ThemeEnum.dark : ThemeEnum.light;

  const getInfo = async () => {
    if (!article?.id) {
      console.warn("No article ID found for fetching recommended articles.");
      return;
    }
    try {
      const res = await fetch(
        `/_plat/api/v2/client/recommendArticles/${article.id}`,
      );
      const data = (await res.json()) as BFFResponseType<ArticlePageResponse>;

      setRecommendArticles(data.response.recommendArticles || []);
    } catch (error) {
      console.error("Error fetching recommended articles:", error);
    }
  };

  useEffect(() => {
    void getInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!safeRecommendArticles.length || hasTrackedRecommendViewRef.current)
      return;
    sendDataToGTM({
      event: "tcCustomEvent",
      eventCategory: "recommended article",
      eventAction: "view",
      eventLabel: "recommended article",
    });
    hasTrackedRecommendViewRef.current = true;
  }, [safeRecommendArticles.length, sendDataToGTM]);

  if (!safeRecommendArticles.length) {
    return null;
  }

  return (
    <SectionContainer title="Recommended stories" theme={theme}>
      <div className="grid grid-cols-12">
        {safeRecommendArticles.slice(0, size).map((article, index) => {
          return (
            <ArticleCard
              key={article.id + index}
              className={clsx(
                "col-span-12",
                size == 4 ? "lg:col-span-3" : "lg:col-span-4",
              )}
              article={article}
              theme={theme}
            />
          );
        })}
      </div>
    </SectionContainer>
  );
}
